import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatDate, formatIDR } from "../lib/constants";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function ReceiptVerify() {
  const { txId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/public/receipt/${txId}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [txId]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="receipt-verify-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          <Link to="/">
            <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
          </Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Verifikasi Kwitansi</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Kecamatan Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="receipt-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Beranda
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-10">
        {!data && !notFound && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-800" />
          </div>
        )}

        {notFound && (
          <Card>
            <CardContent className="p-10 text-center" data-testid="receipt-invalid">
              <XCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
              <h1 className="font-heading text-xl font-bold text-slate-900">Kwitansi Tidak Ditemukan</h1>
              <p className="mt-2 text-sm text-slate-500">Kode kwitansi tidak valid atau dokumen tidak diterbitkan oleh sistem LAZISNU Dawe.</p>
            </CardContent>
          </Card>
        )}

        {data && (
          <Card className="border-emerald-200">
            <CardContent className="p-8" data-testid="receipt-valid">
              <div className="text-center mb-6">
                <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-700" />
                <h1 className="font-heading text-xl font-bold text-emerald-900">Kwitansi Terverifikasi Asli</h1>
                <p className="mt-1 text-xs text-slate-500">Dokumen ini diterbitkan oleh Sistem Informasi LAZISNU Dawe</p>
              </div>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Nama Donatur</span>
                  <span className="font-semibold text-slate-900" data-testid="receipt-donor">{data.donor || "-"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Jenis Transaksi</span>
                  <span className="font-semibold text-slate-900" data-testid="receipt-category">{data.category}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Jumlah</span>
                  <span className="font-bold text-emerald-800" data-testid="receipt-amount">{formatIDR(data.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tanggal</span>
                  <span className="font-semibold text-slate-900">{formatDate(data.tx_date)}</span>
                </div>
                {data.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Keterangan</span>
                    <span className="font-semibold text-slate-900 text-right">{data.description}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-slate-200 pt-3">
                  <span className="text-slate-500">No. Kwitansi</span>
                  <span className="font-mono text-xs text-slate-600">{data.tx_id}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
